// Shared table class constants — light default + dark: variants.

export const TABLE_FRAME_CLASS =
  'rounded-lg overflow-hidden shadow-sm border border-slate-300 bg-white'
  + ' dark:border-slate-700 dark:bg-slate-900 dark:shadow-card-dark';

export const TABLE_SCROLL_CLASS = 'overflow-x-auto overflow-y-auto';

export const TABLE_CLASS = 'min-w-full table-auto text-sm text-left';

export const TABLE_HEAD_CLASS =
  'bg-gray-50 text-gray-600 dark:bg-slate-800 dark:text-slate-300';

export const TABLE_HEAD_STICKY_CLASS =
  'bg-gray-50 text-gray-600 sticky top-0 z-10 border-b border-slate-300'
  + ' dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';

export const TABLE_HEAD_CELL_CLASS =
  'px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider';

export const TABLE_ROW_CLASS =
  'bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-100'
  + ' dark:bg-slate-900 dark:hover:bg-slate-800';

export const TABLE_BODY_CLASS =
  'text-gray-800 divide-y divide-slate-200'
  + ' dark:text-slate-100 dark:divide-slate-800';

/** Validation report tables need separate borders so per-row left status bars render on every row. */
export const VALIDATION_REPORT_TABLE_CLASS =
  `${TABLE_CLASS} border-separate border-spacing-0`;

/** Row background only (status bar lives on the first cell, left of the name / device id). */
export const VALIDATION_REPORT_ROW_OK_CLASS = 'bg-white dark:bg-slate-900';

export const VALIDATION_REPORT_ROW_FAIL_CLASS = 'bg-red-50 dark:bg-red-950/40';

const VALIDATION_REPORT_FIRST_CELL_BAR_BASE =
  'border-y-0 border-r-0 border-t-0 border-solid border-l-4';

/** First column body cell — green bar immediately left of the label. */
export const VALIDATION_REPORT_FIRST_CELL_OK_CLASS =
  `${VALIDATION_REPORT_FIRST_CELL_BAR_BASE} !border-l-green-500 dark:!border-l-green-400`;

/** First column body cell — red bar immediately left of the label. */
export const VALIDATION_REPORT_FIRST_CELL_FAIL_CLASS =
  `${VALIDATION_REPORT_FIRST_CELL_BAR_BASE} !border-l-red-500 dark:!border-l-red-400`;

import type { AssignedDeviceRowHighlightState, AssignedDeviceRowHighlight } from './types/SharedTypes';

export type { AssignedDeviceRowHighlightState, AssignedDeviceRowHighlight };

/** Status bar + matching hover tint; overrides TABLE_ROW_CLASS neutral hover. */
const ASSIGNED_DEVICE_ROW_BASE =
  'bg-white cursor-pointer transition-colors duration-100'
  + ' dark:bg-slate-900'
  + ' border-y-0 border-r-0 border-t-0 border-solid border-l-4';

const ASSIGNED_DEVICE_STALE_ROW_CLASS =
  `${ASSIGNED_DEVICE_ROW_BASE} !border-l-orange-500`
  + ' hover:bg-orange-50 dark:hover:bg-orange-950/40';

const ASSIGNED_DEVICE_VALIDATION_FAIL_ROW_CLASS =
  `${ASSIGNED_DEVICE_ROW_BASE} !border-l-red-500`
  + ' hover:bg-red-50 dark:hover:bg-red-950/40';

const ASSIGNED_DEVICE_OK_ROW_CLASS =
  `${ASSIGNED_DEVICE_ROW_BASE} !border-l-green-500`
  + ' hover:bg-green-50 dark:hover:bg-green-950/40';

export function formatAssignedDeviceStaleTitle(
  staleReason?: string,
): string {
  const reason = String(staleReason ?? '').trim();
  return reason || 'Data stale';
}

/**
 * Row styling for assigned race devices (staleness before validation):
 * stale → orange; fresh + validation fail → red; fresh + pass → green.
 */
export function resolveAssignedDeviceRowHighlight(
  hasValidationMismatch: boolean,
  hasStaleData: boolean,
  options?: {
    mismatchReasons?: string[];
  },
): AssignedDeviceRowHighlight {
  const reasons = (options?.mismatchReasons || []).filter(Boolean);
  if (hasStaleData) {
    const staleReason = reasons.find((reason) => reason.startsWith('Data stale'));
    return {
      state: 'stale',
      className: ASSIGNED_DEVICE_STALE_ROW_CLASS,
      title: formatAssignedDeviceStaleTitle(staleReason),
    };
  }
  if (hasValidationMismatch) {
    return {
      state: 'validation-fail',
      className: ASSIGNED_DEVICE_VALIDATION_FAIL_ROW_CLASS,
      title: reasons.length > 0
        ? `Race validation failed: ${reasons.join('; ')}`
        : 'Race validation failed',
    };
  }
  return {
    state: 'ok',
    className: ASSIGNED_DEVICE_OK_ROW_CLASS,
    title: 'Race validation passed',
  };
}

/** @deprecated Prefer resolveAssignedDeviceRowHighlight for className + title. */
export function getAssignedDeviceRowHighlightClass(
  hasValidationMismatch: boolean,
  hasStaleData: boolean,
): string {
  return resolveAssignedDeviceRowHighlight(hasValidationMismatch, hasStaleData).className;
}
